import React, { useState, useEffect } from 'react';
import validationAuth from '../../validationAuth';
import {
    Container, Typography, Select, MenuItem, FormControl, TextField,
    Button, Grid, Box, Paper, IconButton
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import * as common from '../../commons/common';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import useScrolling from './useScrolling';
import leftArrowIcon from '../../images/left_arrow_icon.svg';
import uploadIcon from '../../images/icon_upload.svg'

const ImprovementRegistration = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [beforeSelect, setBeforeSelect] = useState('');
    const [beforeRemark, setBeforeRemark] = useState('');
    const [facility, setFacility] = useState(null);
    const { id: facilityId } = useParams();
    const { issueId } = location.state || {}; // 수정 모드일 때 전달받는 issueId
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [isCompressing, setIsCompressing] = useState(false);
    const [imageFiles, setImageFiles] = useState([]);
    const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
    const isScrolling = useScrolling(30);

    useEffect(() => {
        const fetchFacility = async () => {
            const token = localStorage.getItem('access_token');
            try {
                const response = await axios.get(`${common.getApiUrl()}/checker/${facilityId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });
                setFacility(response.data);
            } catch (error) {
                common.handleApiError(error);
            }
        };

        if (facilityId) {
            fetchFacility();
        }
    }, [facilityId]);

    // 수정 모드: 기존 이상사항 데이터 로드
    useEffect(() => {
        if (!issueId) return;
        const fetchIssue = async () => {
            const token = localStorage.getItem('access_token');
            try {
                const response = await axios.get(`${common.getApiUrl()}/checker/issue/${issueId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = response.data;
                setBeforeRemark(data.issueContent || '');
                if (data.checkTypeId) setBeforeSelect(String(data.checkTypeId));
                if (data.issueImgSrc && data.issueImgSrc.length > 0) {
                    setImagePreviewUrls(data.issueImgSrc.map(src => common.getImageBaseUrl() + src));
                }
            } catch (error) {
                common.handleApiError(error);
            }
        };
        fetchIssue();
    }, [issueId]);

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            setIsCompressing(true);
            const options = {
                maxSizeMB: 1,
                maxWidthOrHeight: 1920,
                useWebWorker: true
            };

            try {
                const compressedFiles = await Promise.all(
                    files.map(file => imageCompression(file, options))
                );

                setImageFiles(prevFiles => [...prevFiles, ...compressedFiles]);

                const newPreviewUrls = await Promise.all(
                    compressedFiles.map(file => {
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(file);
                        });
                    })
                );

                setImagePreviewUrls(prevUrls => [...prevUrls, ...newPreviewUrls]);
            } catch (error) {
                common.handleApiError(error);
            } finally {
                setIsCompressing(false);
            }
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!beforeRemark.trim()) {
            alert('상세 내용을 입력해주세요.');
            return;
        }

        const formData = new FormData();
        const token = localStorage.getItem('access_token');

        if (issueId) {
            // 수정 모드: PUT API 호출 (multipart/form-data)
            const modifiedIssueRequest = { issueContent: beforeRemark };
            formData.append('modifiedIssueRequest', new Blob([JSON.stringify(modifiedIssueRequest)], { type: 'application/json' }));
            imageFiles.forEach(file => formData.append('uploadFiles', file));
            try {
                await axios.put(`${common.getApiUrl()}/checker/issue/${issueId}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                alert('이상사항이 수정되었습니다.');
                navigate(-1);
            } catch (error) {
                common.handleApiError(error);
            }
        } else {
            // 등록 모드: POST API 호출
            const insertIssueRequest = {
                facilityId: Number(facilityId),
                issueContent: beforeRemark,
                checkTypeId: beforeSelect ? Number(beforeSelect) : null,
            };
            formData.append('insertIssueRequest', new Blob([JSON.stringify(insertIssueRequest)], { type: 'application/json' }));
            imageFiles.forEach(file => formData.append('uploadFiles', file));
            try {
                await axios.post(`${common.getApiUrl()}/checker/issue`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                alert('이상사항 등록되었습니다.');
                navigate('/checker/improvement-registration-list');
            } catch (error) {
                common.handleApiError(error);
            }
        }
    };


    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}년 ${month}월 ${day}일`;
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: 'var(--main-white-color)',
        }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                width: '100%',
                backgroundColor: 'var(--main-blue-color)',
                height: isScrolling ? '60px' : '160px',
                position: 'fixed',
                top: 0,
                zIndex: 1100,
                transition: 'height 0.2s ease',
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: '20px',
                    left: '7px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    borderRadius: '15px',
                    padding: '0 2px 0 2px',
                    marginLeft: '0.5rem'
                }}
                     onClick={() => navigate(-1)}
                >
                    <IconButton sx={{ padding: '4px', paddingLeft: !isScrolling ? '10px' : '4px' }}>
                        <img src={leftArrowIcon} alt="뒤로가기" style={{ width: '15px', height: '15px' }} />
                    </IconButton>
                    {!isScrolling && (
                        <Typography sx={{ fontSize: '1.1rem', color: 'var(--main-blue-color)', paddingRight:'15px' }}>이전</Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#fff',
                    paddingTop: isScrolling ? '0px' : '10px',
                    transition: 'padding-top 0.3s ease',
                }}>
                    <Typography variant="h4" sx={{
                        fontSize: isScrolling ? '1.2rem' : '1.8rem',
                        transition: 'font-size 0.3s ease',
                        textAlign: 'center',
                        padding: '20px 20px 5px 20px'
                    }}>
                        {facility?.facilityNm}
                    </Typography>
                </Box>
            </Box>
            <Container maxWidth="lg" sx={{
                padding: '0',
                flexGrow: 1,
                marginTop: isScrolling ? '100px' : '170px',
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: '100%',
                    padding: '10px 17px',
                    color: '#696969',
                }}>
                    <Typography sx={{ fontSize: '0.9rem' }}>
                        작성일시 : <span style={{  }}>{formatDate(new Date())}</span>
                    </Typography>
                </Box>
                <Paper sx={{ p: 2, mb: 2, mt: 1, boxShadow: 'none', borderRadius:'15px'}}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '10px',
                                        backgroundColor: '#fff',
                                        minHeight: 140,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        padding: '10px',
                                        position: 'relative'
                                    }}
                                    onClick={() => document.getElementById('before-image-upload').click()}
                                >
                                    {imagePreviewUrls.length > 0 && (
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setImageFiles([]);
                                                setImagePreviewUrls([]);
                                            }}
                                            sx={{
                                                position: 'absolute',
                                                top: 5,
                                                right: 5,
                                                padding: '4px',
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: '1.2rem', color: 'var(--main-blue-color)', border: '1px solid var(--main-blue-color)', borderRadius: '20px' }} />
                                        </IconButton>
                                    )}
                                    {imagePreviewUrls.length === 0 && !isCompressing && (
                                        <img
                                            src={uploadIcon}
                                            alt="이미지 업로드"
                                            style={{
                                                width: '45px',
                                                height: '45px',
                                                marginBottom: '10px',
                                                color: 'var(--main-blue-color)'
                                            }}
                                        />
                                    )}
                                    <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        gap: '10px',
                                    }}>
                                        {imagePreviewUrls.map((url, index) => (
                                            <img
                                                key={index}
                                                src={url}
                                                alt={`Preview ${index + 1}`}
                                                style={{ width: '130px', height: '130px', objectFit: 'cover' }}
                                            />
                                        ))}
                                    </Box>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none', backgroundColor: "#fff" }}
                                        id="before-image-upload"
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                    />
                                    {imagePreviewUrls.length === 0 && !isCompressing && (
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            sx={{
                                                color: '#5B5B5B',
                                                fontSize: '1.0rem',
                                                textAlign: 'center'
                                            }}
                                        >
                                            이미지를 업로드하시려면<br />여기를 클릭하세요
                                        </Typography>
                                    )}
                                    {isCompressing && (
                                        <Typography
                                            variant="body2"
                                            component="span"
                                            sx={{
                                                color: '#21509E',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                position: 'absolute',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                textAlign: 'center'
                                            }}
                                        >
                                            이미지 압축 중...
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth variant="outlined" sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderRadius: '10px',
                                            borderColor: '#e0e0e0'
                                        },
                                    },
                                }}>
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight:'bold', mb:1 }}>점검유형</Typography>
                                    <Select
                                        value={beforeSelect}
                                        onChange={(event) => setBeforeSelect(event.target.value)}
                                        IconComponent={(props) => (
                                            beforeSelect ?
                                                <ExpandLessIcon {...props} style={{ color: '#1C6DFF', fontSize: '28px', marginRight: '10px' }} /> :
                                                <ExpandMoreIcon {...props} style={{ color: '#1C6DFF', fontSize: '28px', marginRight: '10px' }} />
                                        )}
                                    >
                                        {[...new Set(facility?.checkItems?.map((item) => item.checkTypeNm))].map((type, index) => (
                                            <MenuItem key={index}
                                                      value={facility.checkItems.find(item => item.checkTypeNm === type).checkTypeId}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{marginBottom: '10px',}}>
                                <Typography sx={{ fontSize: '1.1rem', fontWeight:'bold', mb:1 }}>상세 입력</Typography>
                                <TextField
                                    multiline
                                    rows={1}
                                    value={beforeRemark}
                                    autoComplete="off"
                                    onChange={(event) => setBeforeRemark(event.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderRadius: '10px',
                                                borderColor: '#e0e0e0'
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </Paper>
            </Container>
            {facility && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        width: '100%',
                        py: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        left: 0,
                        px: '20px',
                        zIndex: 1300,
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        sx={{
                            maxWidth: '600px',
                            fontSize: '2.0rem',
                            boxShadow: 'none',
                            borderRadius: '35px',
                            fontWeight: 'normal',
                            backgroundColor: 'var(--main-blue-color)',
                        }}
                        onClick={handleSubmit}
                    >
                        {issueId ? '수정완료' : '작성완료'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
export default validationAuth(ImprovementRegistration);